import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import vlog from '../../javascript/vlog';

// Resolves the V2 profile slug for the current V1 user before rendering the
// wrapped component. The copied V2 my_inbox/ React tree reads
// globalThis.Qwoted.CURRENT_SLUG as if it were the V2 profile slug — used as
// receiver_slug against PostgREST views and as receiver_profile_slug in the
// Supabase Realtime filter. V1 user slugs and V2 profile slugs are
// independently generated, so we look up the V2 profile slug by V1 user id
// (= user_migrate_id in V2) and write the result into globalThis.Qwoted.
const STATE_LOADING = 'loading';
const STATE_READY = 'ready';
const STATE_ERROR = 'error';
// 0 rows from PostgREST single() — V1 user has no corresponding V2 profile yet.
// Most often a brand-new signup who hasn't completed onboarding.
const STATE_NO_PROFILE = 'no_profile';
// PostgREST error code emitted by .single() when 0 rows match — distinct from
// a real transient/server error, and needs a different UX (onboarding CTA, not
// "refresh").
const PGRST_NO_ROWS_CODE = 'PGRST116';
// Brand-new signups can hit PGRST116 transiently — the V2 profile exists but
// hasn't propagated through the V1→V2 sync / PostgREST layer yet. Retry a few
// times with backoff before falling through to the "no profile" CTA, so users
// who really *are* onboarded don't see a misleading "complete onboarding" prompt.
const RETRY_DELAYS_MS = [1000, 3000, 6000];

// A backgrounded or long-idle tab's in-flight fetch gets killed by the browser/OS (tab discard,
// sleep/wake, network handoff). The browser throws a native TypeError ("Failed to fetch" /
// "Load failed" / "NetworkError…") or an AbortError; supabase-js wraps the same condition into a
// result whose error.message is "TypeError: Failed to fetch" (with status 0 — see the call site).
// These self-heal on remount, so they are noise rather than actionable errors. We match the abort
// name and the known network-failure messages — NOT every TypeError — so a real bug still reports.
const isTransientFetchError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return /failed to fetch|load failed|network ?error|aborterror|the (operation|user) (was )?aborted/i.test(message);
};

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

function withV2ProfileSlug(WrappedComponent) {
  function ProfileSlugResolver(props) {
    const { userId } = props;
    const [state, setState] = useState(STATE_LOADING);

    useEffect(() => {
      let cancelled = false;

      const queryProfile = () => globalThis.Qwoted.supabaseClient
        .from('user_profiles')
        // profile_id is the stable integer key the inbox filters by (receiver_profile_id);
        // slug is a friendly ID that changes on rename and must not be used as a key.
        .select('profile_id, slug, type_of_profile')
        .eq('user_migrate_id', userId)
        .single();

      const fetchSlug = async () => {
        try {
          // Init is async and fired fire-and-forget from show.html.erb. Await it so
          // we don't race ahead of `window.supabaseClient` being set; idempotent.
          if (window.initializeSupabaseClient) {
            await window.initializeSupabaseClient();
          }

          // Retry both PGRST116 ("no row" — a brand-new signup's V2 profile can take a few seconds to
          // surface) and a transient fetch abort (status 0 / "Failed to fetch"), which usually self-heals
          // on retry. Only a failure that survives every retry falls through and reports; real server
          // errors (a real HTTP status) are not retried and fail fast.
          // Sequential awaits are intentional: each retry must wait for the previous response + delay.
          /* eslint-disable no-await-in-loop */
          let result = await queryProfile();
          let attempt = 0;
          while (
            !cancelled
            && attempt < RETRY_DELAYS_MS.length
            && (result.error?.code === PGRST_NO_ROWS_CODE || (!result.error && !result.data?.slug)
              || result.status === 0 || isTransientFetchError(result.error))
          ) {
            await sleep(RETRY_DELAYS_MS[attempt]);
            if (cancelled) return;
            result = await queryProfile();
            attempt += 1;
          }
          /* eslint-enable no-await-in-loop */
          if (cancelled) return;

          const { data, error } = result;

          if (error?.code === PGRST_NO_ROWS_CODE || (!error && !data?.slug)) {
            setState(STATE_NO_PROFILE);
            return;
          }

          if (error) {
            // Reached only after retries are exhausted: a transient abort that never self-healed is a
            // persistent failure (e.g. a network/egress block), so it IS the signal worth reporting —
            // not the transient noise we already retried away above.
            vlog.error('Failed to resolve V2 profile slug', { userId, error });
            window.Rollbar?.error('V2 profile slug resolution failed', { userId, error });
            setState(STATE_ERROR);
            return;
          }

          globalThis.Qwoted.CURRENT_SLUG = data.slug;
          // The stable integer profile id, used as receiver_profile_id in the conversation
          // list query. Prefer this over the slug, which is a friendly ID that changes on
          // profile rename (a rename would otherwise hide the user's pre-rename conversations).
          globalThis.Qwoted.CURRENT_PROFILE_ID = data.profile_id;
          // Mirror V2's bridge: my_inbox React reads CURRENT_PROFILE_TYPE to gate
          // media-only UI (e.g. the Focused mailbox tab).
          globalThis.Qwoted.CURRENT_PROFILE_TYPE = data.type_of_profile;
          setState(STATE_READY);
        } catch (err) {
          if (cancelled) return;
          // A native abort thrown during init (which does its own fetch) is transient noise; other
          // throws here are mostly real init/programming errors and still report.
          if (isTransientFetchError(err)) {
            vlog.debug('V2 profile slug resolution aborted (transient)', err);
          } else {
            vlog.error('Failed to resolve V2 profile slug', err);
            window.Rollbar?.error('V2 profile slug resolution failed', err);
          }
          setState(STATE_ERROR);
        }
      };

      fetchSlug();
      return () => {
        cancelled = true;
      };
    }, [userId]);

    if (state === STATE_LOADING) {
      return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p className="text-muted">Loading your inbox…</p>
          </div>
        </div>
      );
    }

    if (state === STATE_NO_PROFILE) {
      return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center px-4">
            <p className="paragraph-lg fw-semibold text-body mb-1">Finish setting up your account</p>
            <p className="paragraph-md text-secondary mb-3">
              Complete your profile to start using messaging.
            </p>
            <a href="/onboarding/new" className="btn btn-primary btn-sm" style={{ padding: '12px 16px' }}>
              Complete onboarding
            </a>
          </div>
        </div>
      );
    }

    if (state === STATE_ERROR) {
      return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="alert alert-warning text-center" role="status">
            We can&apos;t load your inbox right now. Please refresh in a moment.
          </div>
        </div>
      );
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent {...props} />;
  }

  ProfileSlugResolver.propTypes = {
    userId: PropTypes.number.isRequired,
  };

  return ProfileSlugResolver;
}

export default withV2ProfileSlug;
